import { useEffect, useRef } from 'react';

/**
 * A background clip that loops without a visible restart.
 *
 * Two stacked copies of the same file share one download; the incoming copy is
 * crossfaded in shortly before the outgoing one ends, which hides the seam a
 * plain `loop` would show. The gradient sits behind throughout, so a slow or
 * failed load leaves colour rather than black — and under
 * `prefers-reduced-motion` the clip never plays at all.
 */
export function LoopingVideo({
  src,
  fallback,
  fadeSeconds = 1.2,
  objectPosition = 'center',
}: {
  src: string;
  fallback: string;
  fadeSeconds?: number;
  /** Which part of the clip to keep when it is cropped to fit, e.g. 'center top'. */
  objectPosition?: string;
}) {
  const host = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;

    const make = (opacity: number) => {
      const v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.loop = false;
      v.preload = 'auto';
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('aria-hidden', 'true');
      v.style.cssText = `position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:${objectPosition};display:block;opacity:${opacity};transition:opacity ${fadeSeconds}s linear;`;
      el.appendChild(v);
      return v;
    };

    const a = make(1);
    const b = make(0);
    let front = a;
    let back = b;
    let armed = false;

    // Stream the first copy immediately; the second only starts loading once
    // the first can play, so the file is never pulled twice up front.
    a.src = src;
    void a.play().catch(() => undefined);
    a.addEventListener(
      'canplay',
      () => {
        if (!b.src) {
          b.src = src;
          b.load();
        }
      },
      { once: true },
    );

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const duration = front.duration;
      if (!duration || !Number.isFinite(duration)) return;
      if (armed || duration - front.currentTime > fadeSeconds) return;

      if (back.readyState < 3) {
        // The incoming copy cannot decode yet — restart this one rather than
        // fade to nothing.
        if (duration - front.currentTime <= 0.06) {
          front.currentTime = 0;
          void front.play().catch(() => undefined);
        }
        return;
      }

      armed = true;
      back.currentTime = 0;
      void back.play().catch(() => undefined);
      back.style.opacity = '1';
      front.style.opacity = '0';

      const outgoing = front;
      front = back;
      back = outgoing;
      window.setTimeout(() => {
        back.pause();
        armed = false;
      }, fadeSeconds * 1000);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      a.remove();
      b.remove();
    };
  }, [src, fadeSeconds, objectPosition]);

  return <div ref={host} style={{ position: 'absolute', inset: 0, background: fallback }} />;
}
